import { useState } from "react";

import {
  Loader2,
  AlertCircle,
  Info,
  ExternalLink,
  Trash2,
  ChevronDown,
  ChevronUp,
  Zap,
} from "lucide-react";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CopyableCell } from "@/components/ui/copyable-cell";
import { DeleteConfirmDialog } from "@/components/ui/delete-confirm-dialog";

import { CustomDomain } from "@/lib/api/custom-domains";
import {
  classifyVerification,
  PROPAGATING_GRACE_MS,
  ACTIONABLE_GRACE_MS,
} from "@/lib/custom-domains-verification";
import { cn } from "@/lib/utils";

/* ----------------------------- status indicator ---------------------------- */
/* One calm dot/spinner + label. Colour is carried by the dot; the label stays
   neutral except for the two states that genuinely need emphasis (live = green,
   failed = red). No tinted boxes. */

type StatusKind = "live" | "active" | "waiting" | "progress" | "failed";

function StatusIndicator({ kind, label }: { kind: StatusKind; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium">
      {kind === "progress" ? (
        <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
      ) : (
        <span
          className={cn(
            "h-1.5 w-1.5 rounded-full",
            kind === "live" && "bg-emerald-500",
            kind === "active" && "bg-emerald-500",
            kind === "failed" && "bg-destructive",
            kind === "waiting" && "bg-amber-500",
          )}
        />
      )}
      <span
        className={cn(
          kind === "live" && "text-emerald-600 dark:text-emerald-400",
          kind === "active" && "text-foreground",
          kind === "failed" && "text-destructive",
          kind === "waiting" && "text-amber-600 dark:text-amber-400",
          kind === "progress" && "text-blue-600 dark:text-blue-400",
        )}
      >
        {label}
      </span>
    </span>
  );
}

/* ------------------------------- DNS records -------------------------------- */
/* A single Type / Name / Value table — CNAME + TXT together (the Type column
   distinguishes them). Monospace, copy-on-click via the shared CopyableCell. */

function DnsRecords({ records }: { records: CustomDomain["dnsRecords"] }) {
  if (!records || records.length === 0) return null;
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <table className="w-full table-fixed text-xs">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-[10px] uppercase tracking-wide text-muted-foreground">
            <th className="w-[68px] px-3 py-2 font-medium">Type</th>
            <th className="w-[36%] px-3 py-2 font-medium">Name</th>
            <th className="px-3 py-2 font-medium">Value</th>
          </tr>
        </thead>
        <tbody>
          {records.map((record, index) => (
            <tr key={index} className="border-b border-border last:border-0">
              <td className="px-3 py-2 align-middle">
                <Badge
                  variant="secondary"
                  className="rounded-md px-1.5 py-0 font-mono text-[10px] font-semibold uppercase"
                >
                  {record.type}
                </Badge>
              </td>
              <td className="min-w-0 px-3 py-1.5 align-middle">
                <CopyableCell
                  value={record.name}
                  tooltipLabel="Copy name"
                  iconSize={13}
                  display={
                    <span
                      className="block min-w-0 flex-1 truncate font-mono text-foreground"
                      title={record.name}
                    >
                      {record.name}
                    </span>
                  }
                />
              </td>
              <td className="min-w-0 px-3 py-1.5 align-middle">
                <CopyableCell
                  value={record.value}
                  tooltipLabel="Copy value"
                  iconSize={13}
                  display={
                    <span
                      className="block min-w-0 flex-1 truncate font-mono text-foreground"
                      title={record.value}
                    >
                      {record.value}
                    </span>
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* --------------------------------- card ------------------------------------- */

export interface DomainCardProps {
  domain: CustomDomain;
  isAutoChecking: boolean;
  /** Connected via Entri (DNS written for the user) — hide the manual "Stop". */
  viaEntri?: boolean;
  isVerifying: boolean;
  isDeleting: boolean;
  isSettingPrimary: boolean;
  entriEnabled: boolean;
  onConnectEntri: () => Promise<void>;
  onVerify: () => void;
  onStopAutoCheck: () => void;
  onSetPrimary: () => void;
  onDelete: () => void | Promise<void>;
}

export function DomainCard({
  domain,
  isAutoChecking,
  viaEntri = false,
  isVerifying,
  isDeleting,
  isSettingPrimary,
  entriEnabled,
  onConnectEntri,
  onVerify,
  onStopAutoCheck,
  onSetPrimary,
  onDelete,
}: DomainCardProps) {
  // When Entri is available, the manual DNS records start collapsed behind a
  // "Set up DNS manually" disclosure (Entri is the primary path).
  const [showDNS, setShowDNS] = useState(
    !entriEnabled &&
      (domain.status === "pending" || domain.status === "verifying"),
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const handleConnect = async (): Promise<void> => {
    setConnecting(true);
    try {
      await onConnectEntri();
    } catch {
      // SDK failed to load / unavailable — reveal the manual fallback.
      setShowDNS(true);
      toast.error(
        "Couldn't open the domain connector. Add the DNS records manually below.",
      );
    } finally {
      setConnecting(false);
    }
  };

  // Status precedence (unchanged): Primary → in-progress → status map.
  const isProvisioningSsl =
    domain.status === "active" && domain.sslStatus !== "active";
  const isInProgress =
    isProvisioningSsl ||
    ((isVerifying || isAutoChecking) &&
      domain.status !== "active" &&
      domain.status !== "failed");

  const status: { kind: StatusKind; label: string } =
    domain.isPrimary && domain.status === "active"
      ? { kind: "live", label: "Live" }
      : isInProgress
        ? {
            kind: "progress",
            label: isProvisioningSsl ? "Provisioning SSL" : "Checking DNS",
          }
        : domain.status === "active"
          ? { kind: "active", label: "Active" }
          : domain.status === "failed"
            ? { kind: "failed", label: "Failed" }
            : { kind: "waiting", label: "Waiting for DNS" };

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      {/* Header: domain + status, delete */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
          {domain.status === "active" ? (
            <a
              href={`https://${domain.domain}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-1.5 font-mono text-sm font-medium text-foreground transition-colors hover:text-primary"
            >
              {domain.domain}
              <ExternalLink className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
            </a>
          ) : (
            <span className="font-mono text-sm font-medium text-foreground">
              {domain.domain}
            </span>
          )}
          <StatusIndicator kind={status.kind} label={status.label} />
        </div>

        <Button
          variant="ghost"
          size="icon"
          onClick={() => setShowDeleteConfirm(true)}
          disabled={isDeleting}
          title="Remove domain"
          className="-mr-1.5 -mt-1 h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
        >
          {isDeleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
        </Button>
      </div>

      <DeleteConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        items={[domain]}
        getItemLabel={(d) => d.domain}
        entityName="domain"
        title="Remove this domain?"
        description={
          domain.isPrimary
            ? "This is your live domain — removing it takes the project offline here. You are about to permanently remove:"
            : undefined
        }
        isDeleting={isDeleting}
        onConfirm={async () => {
          await onDelete();
        }}
      />

      {/* Verification feedback — classified, never a raw CF dump. Transient
          (propagation) errors get a calm muted note only after a long grace;
          genuine config errors get an actionable amber note pointing at the
          DNS-records disclosure below. `failed` is handled separately (terminal). */}
      {(() => {
        if (domain.status === "failed") return null;
        const ageMs = Date.now() - new Date(domain.createdAt).getTime();
        const kind = classifyVerification(domain.verificationErrors);

        if (kind === "propagating" && ageMs > PROPAGATING_GRACE_MS) {
          return (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                Still waiting on your DNS provider. Propagation can take up to an
                hour on some registrars — we'll keep checking automatically.
              </span>
            </div>
          );
        }

        if (kind === "actionable" && ageMs > ACTIONABLE_GRACE_MS) {
          return (
            <div
              className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2 text-xs text-amber-700 dark:text-amber-400"
              title={domain.verificationErrors?.join(", ")}
            >
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                We can't confirm your DNS yet. Open "DNS records" below and make
                sure every record is added — the CNAME must be DNS-only (not
                proxied).
              </span>
            </div>
          );
        }

        return null;
      })()}

      {/* Pending — connect / manual DNS / verify */}
      {domain.status === "pending" && (
        <div className="mt-4 space-y-3">
          {entriEnabled && !isAutoChecking && (
            <Button
              onClick={handleConnect}
              disabled={connecting}
              className="h-9 w-full"
            >
              {connecting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Zap className="mr-1.5 h-4 w-4" />
                  Connect domain
                </>
              )}
            </Button>
          )}

          {isAutoChecking && (
            <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
              <span>
                {viaEntri
                  ? "DNS configured — we'll activate automatically."
                  : "We'll mark it active automatically."}
              </span>
              {!viaEntri && (
                <button
                  onClick={onStopAutoCheck}
                  className="shrink-0 text-xs font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                >
                  Stop
                </button>
              )}
            </div>
          )}

          {/* Manual DNS disclosure */}
          <button
            onClick={() => setShowDNS(!showDNS)}
            className="flex w-full items-center justify-between rounded-lg border border-border bg-muted/30 px-3 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted/50"
          >
            <span>{entriEnabled ? "Set up DNS manually" : "DNS records"}</span>
            {showDNS ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>

          {showDNS && (
            <div className="space-y-2.5">
              <p className="text-xs text-muted-foreground">
                Add these records at your registrar, then verify.
              </p>
              <DnsRecords records={domain.dnsRecords} />
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onVerify}
                  disabled={isVerifying}
                  className="h-8"
                >
                  {isVerifying ? (
                    <>
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      Checking…
                    </>
                  ) : (
                    "Verify"
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Verifying */}
      {domain.status === "verifying" && (
        <div className="mt-3 flex items-center justify-between gap-3 text-sm text-muted-foreground">
          {isAutoChecking ? (
            <>
              <span>
                {viaEntri
                  ? "DNS configured — we'll activate automatically."
                  : "We'll mark it active automatically."}
              </span>
              {!viaEntri && (
                <button
                  onClick={onStopAutoCheck}
                  className="shrink-0 text-xs font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                >
                  Stop
                </button>
              )}
            </>
          ) : (
            <>
              <span>Auto-check paused.</span>
              <Button
                variant="outline"
                size="sm"
                onClick={onVerify}
                disabled={isVerifying}
                className="h-8 shrink-0"
              >
                {isVerifying ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    Checking…
                  </>
                ) : (
                  "Verify"
                )}
              </Button>
            </>
          )}
        </div>
      )}

      {/* Active + SSL ready */}
      {domain.status === "active" && domain.sslStatus === "active" && (
        <>
          {!domain.isPrimary && (
            <div className="mt-3 flex items-center justify-between gap-3">
              <span className="text-sm text-muted-foreground">
                Verified with SSL.
              </span>
              <Button
                size="sm"
                onClick={onSetPrimary}
                disabled={isSettingPrimary}
                className="h-8 shrink-0"
              >
                {isSettingPrimary ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    Setting…
                  </>
                ) : (
                  "Use this domain"
                )}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Active but SSL still provisioning. With HTTP DCV the cert validates over
          the proxied connection — no user-managed SSL record is needed, so it
          normally issues on its own within minutes. We still expose the DNS
          records (mainly the CNAME) behind a disclosure so a genuinely stuck cert
          (CNAME missing/not proxied) isn't a dead end. */}
      {domain.status === "active" && domain.sslStatus !== "active" && (
        <div className="mt-3 space-y-3">
          <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
            <span>
              Issuing the SSL certificate (usually 5–10 min) — we'll activate it
              automatically.
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={onVerify}
              disabled={isVerifying}
              className="h-8 shrink-0"
            >
              {isVerifying ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                "Refresh"
              )}
            </Button>
          </div>

          {/* The common case (cert just issuing) auto-resolves, so keep this
              state calm — the recovery tools (manual records + Entri re-push)
              for a genuinely stuck cert live behind the disclosure, not as a
              prominent "Connect" CTA up top. */}
          <button
            onClick={() => setShowDNS(!showDNS)}
            className="flex w-full items-center justify-between rounded-lg border border-border bg-muted/30 px-3 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted/50"
          >
            <span>DNS records</span>
            {showDNS ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>

          {showDNS && (
            <div className="space-y-2.5">
              <p className="text-xs text-muted-foreground">
                The certificate issues automatically — just make sure the CNAME
                below is present at your registrar (and set to DNS-only, not
                proxied).
              </p>
              <DnsRecords records={domain.dnsRecords} />
              {entriEnabled && (
                <Button
                  onClick={handleConnect}
                  disabled={connecting}
                  className="h-9 w-full"
                >
                  {connecting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Zap className="mr-1.5 h-4 w-4" />
                      Connect domain
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Failed */}
      {domain.status === "failed" && domain.verificationErrors && (
        <Alert variant="destructive" className="mt-3">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-xs">
            {domain.verificationErrors.join(", ")}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
